/**
 * DepartmentsPage Component
 * List all departments with search, filters, and server-side pagination
 */

import { useState, useEffect } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DepartmentRow } from '../components/departments/DepartmentRow';
import { DepartmentFormModal } from '../components/departments/DepartmentFormModal';
import { DeleteConfirmModal } from '../components/ui/DeleteConfirmModal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Pagination } from '../components/ui/Pagination';
import { departmentsService } from '../services/departments.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { usePaginationParams } from '../hooks/usePaginationParams';

export const DepartmentsPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Pagination state from URL
  const { params, setPage, setLimit, setSearch, clearFilters, correctPageIfNeeded } = usePaginationParams({
    defaultLimit: 25,
    defaultSort: 'name:asc',
    scrollToTop: true,
  });

  // Local search input state (for debouncing)
  const [searchInput, setSearchInput] = useState(params.search);

  // Cleanup department image cache on unmount
  useEffect(() => {
    return () => {
      departmentsService.clearDepartmentImageCache();
    };
  }, []);

  // Debounced search - update URL params after 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== params.search) {
        setSearch(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setSearch]);

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [editingDeptId, setEditingDeptId] = useState<number | undefined>();
  const [deletingDeptId, setDeletingDeptId] = useState<number | undefined>();
  const [deletingDeptName, setDeletingDeptName] = useState('');

  // Fetch departments with server-side pagination
  const { data: deptsResponse, isLoading, isFetching, error: deptsError, dataUpdatedAt } = useQuery({
    queryKey: ['departments', params],
    queryFn: () => departmentsService.getPaginated({
      page: params.page,
      limit: params.limit,
      search: params.search || undefined,
      sort: params.sort,
    }),
    placeholderData: keepPreviousData,
  });

  // Extract data and meta from response
  const departments = deptsResponse?.data ?? [];
  const meta = deptsResponse?.meta ?? { page: 1, limit: 25, total: 0, total_pages: 1 };

  // Correct page if it exceeds total pages
  useEffect(() => {
    if (deptsResponse?.meta) {
      correctPageIfNeeded(deptsResponse.meta.total_pages);
    }
  }, [deptsResponse?.meta, correctPageIfNeeded]);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => departmentsService.delete(id),
    onSuccess: () => {
      toast.success('Department deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setIsDeleteModalOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete department');
    },
  });

  // Modal handlers
  const handleAddDepartment = () => {
    setFormMode('add');
    setEditingDeptId(undefined);
    setIsFormOpen(true);
  };

  const handleEditDepartment = (deptId: number) => {
    setFormMode('edit');
    setEditingDeptId(deptId);
    setIsFormOpen(true);
  };

  const handleDeleteDepartment = (deptId: number) => {
    const dept = departments.find((d) => d.id === deptId);
    if (dept) {
      setDeletingDeptId(deptId);
      setDeletingDeptName(dept.name);
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDelete = () => {
    if (deletingDeptId) {
      deleteMutation.mutate(deletingDeptId);
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    clearFilters();
  };

  // Check if any filters are active
  const hasActiveFilters = params.search;

  if (deptsError) {
    return (
      <Layout>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-yellow-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-medium text-heading mb-2">Failed to load departments</h3>
          <p className="text-meta mb-4">There was an error loading the departments list.</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['departments'] })}>
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-heading flex items-center">
              <svg className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Departments
            </h1>
          </div>
          {hasAdminAccess(user?.role) && (
            <Button onClick={handleAddDepartment}>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Department
            </Button>
          )}
        </div>

        {/* Filters - Desktop (>= lg) */}
        <div className="hidden lg:block bg-surface rounded-lg shadow p-4">
          <div className="flex items-end gap-8">
            <div className="flex-1 max-w-[22%]">
              <label htmlFor="search-departments" className="block text-xs font-bold text-body mb-1">
                Search Departments
              </label>
              <Input
                id="search-departments"
                type="search"
                placeholder="Search by name or description..."
                value={searchInput}
                onChange={handleSearchInputChange}
              />
            </div>
            <div className="ml-auto">
              <Button
                variant="tertiary"
                onClick={handleClearFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - Mobile (< lg) */}
        <div className="lg:hidden bg-surface rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label htmlFor="search-departments-mobile" className="block text-xs font-bold text-body mb-1">
                Search Departments
              </label>
              <Input
                id="search-departments-mobile"
                type="search"
                placeholder="Search by name or description..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="text-base px-4 py-3 sm:text-sm sm:px-3 sm:py-2"
              />
            </div>
            <Button variant="tertiary" onClick={handleClearFilters} className="flex items-center gap-2">
              <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Page Info */}
        {meta.total > 0 && (
          <div className="text-sm text-meta">
            Page {meta.page} of {meta.total_pages}{'\u00A0\u00A0'}&bull;{'\u00A0\u00A0'}{meta.total} department{meta.total !== 1 ? 's' : ''} total
          </div>
        )}

        {/* Departments List */}
        <div className="relative">
          {/* Loading overlay */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-surface/50 z-10 flex items-center justify-center">
              <LoadingSpinner size="md" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading departments..." />
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-meta mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-lg font-medium text-heading mb-2">
                {hasActiveFilters ? 'No departments found' : 'No departments yet'}
              </h3>
              <p className="text-meta mb-4">
                {hasActiveFilters ? 'Try adjusting your search.' : 'Get started by creating your first department.'}
              </p>
              {hasAdminAccess(user?.role) && !hasActiveFilters && (
                <Button onClick={handleAddDepartment}>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create First Department
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-surface rounded-lg shadow divide-y divide-[var(--border-default)]">
              {departments.map((dept) => (
                <DepartmentRow
                  key={dept.id}
                  department={dept}
                  onEdit={handleEditDepartment}
                  onDelete={handleDeleteDepartment}
                  refreshTrigger={dataUpdatedAt}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.total_pages}
            entityName="departments"
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isFetching}
          />
        )}
      </div>

      {/* Modals */}
      <DepartmentFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        mode={formMode}
        departmentId={editingDeptId}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Department"
        message="Are you sure you want to delete this department? The department must be empty (no teams or members)."
        itemName={deletingDeptName}
        isDeleting={deleteMutation.isPending}
      />
    </Layout>
  );
};
